import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import { Header } from 'components/Header/Header';
import { newServer } from 'constants/constants';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';

/**
 * Interruptores de proveedores de pago.
 *
 * Solo para SUPER_ADMIN (rol 9). Deshabilitar un proveedor afecta a TODOS los
 * onboarding nuevos: quien ya eligio un proveedor sigue su curso normal, porque
 * este cambio no toca ninguna solicitud existente.
 */

interface ProviderRow {
  code: string;
  name: string;
  methods: string[];
  requiresDocuments: boolean;
  description: string;
  enabled: boolean;
}

const PaymentSettingsPage = () => {
  const { user } = useLogin() as any;
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ProviderRow | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const isSuperAdmin = Number(user?.role_id) === 9;

  const load = useCallback(async () => {
    if (!user?.token) {
      // Sin sesion no hay peticion posible: se corta el spinner y se avisa.
      setLoading(false);
      setError('No hay una sesion activa. Vuelve a iniciar sesion para cargar los proveedores.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${newServer}requests/providers`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) throw new Error(`respondio ${response.status}`);
      const body = await response.json();
      setProviders(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError('No se pudieron cargar los proveedores.');
      console.error('[PaymentSettings] load', e);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (provider: ProviderRow, enabled: boolean) => {
    setSavingCode(provider.code);
    setError(null);
    try {
      const response = await fetch(`${newServer}requests/providers/${provider.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        // 409 = dejaria cero proveedores habilitados
        const message = response.status === 409
          ? 'Debe quedar al menos un proveedor habilitado.'
          : 'No se pudo guardar el cambio.';
        setError(message);
        return;
      }

      const body = await response.json();
      setProviders((current) => current.map((item) => (
        item.code === body.data.code ? { ...item, enabled: body.data.enabled } : item
      )));
      setSnackbar(`${provider.name} ${enabled ? 'habilitado' : 'deshabilitado'}.`);
    } catch (e) {
      setError('No se pudo guardar el cambio.');
      console.error('[PaymentSettings] toggle', e);
    } finally {
      setSavingCode(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box style={{ padding: 24, backgroundColor: 'var(--bg)', minHeight: '100vh' }}>
        <Header />
        <Alert severity='warning'>
          Solo un super administrador puede cambiar esta configuracion.
        </Alert>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 24, backgroundColor: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <Typography variant='h5' style={{ fontWeight: 'bold', color: 'var(--text)' }}>
        Payment Providers
      </Typography>
      <Typography variant='body2' style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
        Choose which providers appear in the onboarding. Disabling one only affects new
        sign-ups: lawyers who already picked it keep their current process.
      </Typography>

      {error && (
        <Alert severity='error' style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress style={{ color: 'var(--brand)' }} />
        </Box>
      ) : (
        providers.map((provider) => (
          <Card
            key={provider.code}
            style={{
              marginBottom: 12,
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <CardContent style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Box style={{ flex: 1 }}>
                <Typography variant='h6' style={{ color: 'var(--text)', fontWeight: 'bold' }}>
                  {provider.name}
                </Typography>
                <Box style={{ display: 'flex', gap: 6, margin: '6px 0' }}>
                  {provider.methods.map((method) => (
                    <Chip
                      key={method}
                      label={method}
                      size='small'
                      variant='outlined'
                      style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}
                    />
                  ))}
                </Box>
                <Typography variant='body2' style={{ color: 'var(--text-muted)' }}>
                  {provider.description}
                </Typography>
              </Box>

              {savingCode === provider.code ? (
                <CircularProgress size={24} style={{ color: 'var(--brand)' }} />
              ) : (
                <Switch
                  checked={provider.enabled}
                  inputProps={{ 'aria-label': `Toggle ${provider.name}` }}
                  onChange={(event) => {
                    // Deshabilitar pide confirmacion; habilitar es reversible sin riesgo.
                    if (event.target.checked) {
                      toggle(provider, true);
                    } else {
                      setConfirm(provider);
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>Disable {confirm?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.name} will stop appearing for new sign-ups. Lawyers who already
            chose it are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color='secondary'
            onClick={() => {
              const target = confirm;
              setConfirm(null);
              if (target) toggle(target, false);
            }}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
    </Box>
  );
};

export default PaymentSettingsPage;
